// Service API pour l'admin - Données dynamiques
import API_CONFIG from '../config/api';
// import errorService from './errorService';

class AdminApiService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.timeout = API_CONFIG.TIMEOUT;
  }

  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('auth_token');
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` }),
      ...(options.headers || {})
    };

    // Si c'est FormData, ne pas mettre Content-Type
    if (options.body instanceof FormData) {
      delete headers['Content-Type'];
    }

    const config = {
      timeout: this.timeout,
      headers,
      ...options,
    };

    // Si le body est un objet et non FormData, le convertir en JSON
    if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }

  // Dashboard
  async getDashboardStats() {
    try {
      const response = await this.makeRequest('/admin-dev/dashboard');
      return response.data || response;
    } catch (error) {
      console.warn('Dashboard API not available, using fallback data');
      return {
        totalProducts: 156,
        totalOrders: 89,
        totalRevenue: 2450000,
        activeSuppliers: 12
      };
    }
  }

  async getRecentOrders() {
    try {
      const response = await this.makeRequest('/admin-dev/orders/recent');
      return response.data || response;
    } catch (error) {
      console.warn('Recent orders API not available, using fallback data');
      return [
        {
          id: '#AGR-001',
          customer: 'Marie Kouassi',
          product: 'Tomates',
          quantity: '50kg',
          status: 'completed',
          amount: '25,000',
          date: '2024-01-15'
        },
        {
          id: '#AGR-002',
          customer: 'Jean Traoré',
          product: 'Pommes de Terre',
          quantity: '100kg',
          status: 'pending',
          amount: '45,000',
          date: '2024-01-14'
        },
        {
          id: '#AGR-003',
          customer: 'Fatou Diallo',
          product: 'Carottes',
          quantity: '30kg',
          status: 'processing',
          amount: '18,000',
          date: '2024-01-13'
        }
      ];
    }
  }

  async getTopProducts() {
    try {
      const response = await this.makeRequest('/admin-dev/products/top');
      return response.data || response;
    } catch (error) {
      console.warn('Top products API not available, using fallback data');
      return [
        {
          id: 1,
          name: 'Tomates Fraîches',
          sales: 156,
          revenue: 78000
        },
        {
          id: 2,
          name: 'Pommes de Terre',
          sales: 134,
          revenue: 67000
        },
        {
          id: 3,
          name: 'Carottes',
          sales: 98,
          revenue: 49000
        }
      ];
    }
  }

  // Recherche
  async search(query) {
    try {
      const response = await this.makeRequest(`/admin/search?q=${encodeURIComponent(query)}`);
      return response.data || response;
    } catch (error) {
      console.warn('Search API not available, using fallback data');
      return {
        products: [
          { id: 1, name: 'Tomates', type: 'product' },
          { id: 2, name: 'Marie Kouassi', type: 'customer' }
        ],
        orders: [
          { id: '#AGR-001', customer: 'Marie Kouassi', status: 'completed' }
        ]
      };
    }
  }

  // Produits
  async getProducts() {
    try {
      const response = await this.makeRequest('/admin-dev/products');
      return response.data || response;
    } catch (error) {
      console.warn('Products API not available, using fallback data');
      return [
        {
          id: 1,
          name: 'Tomates Fraîches',
          price: 800,
          stock_quantity: 50,
          unit: 'kg',
          category: { name: 'Légumes' },
          supplier: { business_name: 'Ferme Kouassi' },
          is_active: true
        },
        {
          id: 2,
          name: 'Pommes de Terre',
          price: 600,
          stock_quantity: 100,
          unit: 'kg',
          category: { name: 'Légumes' },
          supplier: { business_name: 'Ferme Kouassi' },
          is_active: true
        }
      ];
    }
  }

  async createProduct(productData) {
    const isFormData = productData instanceof FormData;
    const response = await this.makeRequest('/admin-dev/products', {
      method: 'POST',
      body: isFormData ? productData : JSON.stringify(productData),
      headers: isFormData ? {} : { 'Content-Type': 'application/json' }
    });
    // Extraire data si la réponse a cette structure
    return response.data || response;
  }

  async updateProduct(id, productData) {
    const isFormData = productData instanceof FormData;

    // Pour les FormData avec fichiers, utiliser POST avec une route dédiée
    // car PUT ne fonctionne pas bien avec les fichiers dans Laravel
    if (isFormData) {
      const response = await this.makeRequest(`/admin-dev/products/${id}/update`, {
        method: 'POST',
        body: productData,
        headers: {} // Ne pas définir Content-Type, le navigateur le fera automatiquement avec FormData
      });
      return response.data || response;
    } else {
      // Pour JSON, utiliser PUT normalement
      const response = await this.makeRequest(`/admin-dev/products/${id}`, {
        method: 'PUT',
        body: JSON.stringify(productData),
        headers: { 'Content-Type': 'application/json' }
      });
      return response.data || response;
    }
  }

  async deleteProduct(id) {
    return await this.makeRequest(`/admin-dev/products/${id}`, {
      method: 'DELETE',
    });
  }

  // Catégories
  async getCategories() {
    try {
      const response = await this.makeRequest('/admin-dev/categories');
      return response.data || response;
    } catch (error) {
      console.warn('API not available, using fallback data');
      return [
        { id: 1, name: 'Légumes' },
        { id: 2, name: 'Fruits' },
        { id: 3, name: 'Céréales' },
        { id: 4, name: 'Épices' }
      ];
    }
  }

  async createCategory(categoryData) {
    const response = await this.makeRequest('/admin-dev/categories', {
      method: 'POST',
      body: JSON.stringify(categoryData),
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data || response;
  }

  async updateCategory(id, categoryData) {
    const response = await this.makeRequest(`/admin-dev/categories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(categoryData),
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data || response;
  }

  async deleteCategory(id) {
    return await this.makeRequest(`/admin-dev/categories/${id}`, {
      method: 'DELETE',
    });
  }

  // Fournisseurs
  async getSuppliers() {
    try {
      const response = await this.makeRequest('/admin-dev/suppliers');
      return response.data || response;
    } catch (error) {
      console.warn('Suppliers API not available, using fallback data:', error);
      return [
        { id: 1, business_name: 'Ferme Kouassi' },
        { id: 2, business_name: 'Verger Diabaté' }
      ];
    }
  }

  // Commandes
  async getOrders(status = 'all') {
    try {
      const url = status === 'all'
        ? '/admin-dev/orders'
        : `/admin-dev/orders?status=${status}`;
      const response = await this.makeRequest(url);
      return response.data || response;
    } catch (error) {
      console.warn('Orders API not available, using fallback data');
      return [];
    }
  }

  async updateOrderStatus(orderId, status) {
    const response = await this.makeRequest(`/admin-dev/orders/${orderId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data || response;
  }

  async assignDeliveryPerson(orderId, deliveryPersonId) {
    const response = await this.makeRequest(`/admin-dev/orders/${orderId}/assign-delivery`, {
      method: 'PUT',
      body: JSON.stringify({ delivery_person_id: deliveryPersonId || null }),
      headers: { 'Content-Type': 'application/json' }
    });
    return response.data || response;
  }

  // Utilisateurs
  async getUsers(userType = 'all') {
    try {
      const url = userType === 'all'
        ? '/admin-dev/users'
        : `/admin-dev/users?user_type=${userType}`;
      const response = await this.makeRequest(url);
      return response.data || response;
    } catch (error) {
      console.warn('Users API not available, using fallback data');
      return [];
    }
  }

  async createUser(userData) {
    const response = await this.makeRequest('/admin-dev/users', {
      method: 'POST',
      body: userData
    });
    return response.data || response;
  }

  async updateUser(id, userData) {
    const response = await this.makeRequest(`/admin-dev/users/${id}`, {
      method: 'PUT',
      body: userData
    });
    return response.data || response;
  }

  async deleteUser(id) {
    const response = await this.makeRequest(`/admin-dev/users/${id}`, {
      method: 'DELETE'
    });
    return response.data || response;
  }

  // Notifications
  async getNotifications() {
    try {
      const response = await this.makeRequest('/admin-dev/notifications');
      console.log('Notifications API response:', response);
      // Si la réponse indique une erreur, retourner un objet avec success: false
      if (response && response.success === false) {
        return { success: false, message: response.message, data: [] };
      }
      // Si la réponse a un champ data, retourner data directement
      if (response && response.data) {
        return response.data;
      }
      // Sinon retourner la réponse directement (si c'est déjà un tableau)
      return response;
    } catch (error) {
      console.warn('Notifications API not available:', error);
      // Retourner un tableau vide en cas d'erreur
      return [];
    }
  }

  async markNotificationAsRead(notificationId) {
    try {
      const response = await this.makeRequest(`/admin-dev/notifications/${notificationId}/read`, {
        method: 'POST'
      });
      return response.data || response;
    } catch (error) {
      console.warn('Mark notification as read failed');
      return { success: false };
    }
  }

  // Fournisseurs
  async createSupplier(supplierData) {
    const response = await this.makeRequest('/admin-dev/suppliers', {
      method: 'POST',
      body: supplierData
    });
    return response.data || response;
  }

  async updateSupplier(id, supplierData) {
    const response = await this.makeRequest(`/admin-dev/suppliers/${id}`, {
      method: 'PUT',
      body: supplierData
    });
    return response.data || response;
  }

  async deleteSupplier(id) {
    const response = await this.makeRequest(`/admin-dev/suppliers/${id}`, {
      method: 'DELETE'
    });
    return response.data || response;
  }

  // Livreurs
  async getDeliveryPersons() {
    try {
      const response = await this.makeRequest('/admin-dev/delivery');
      return response.data || response;
    } catch (error) {
      console.warn('Delivery persons API not available, using fallback data');
      return [];
    }
  }

  async createDeliveryPerson(deliveryData) {
    const response = await this.makeRequest('/admin-dev/delivery', {
      method: 'POST',
      body: deliveryData
    });
    return response.data || response;
  }

  async updateDeliveryPerson(id, deliveryData) {
    const response = await this.makeRequest(`/admin-dev/delivery/${id}`, {
      method: 'PUT',
      body: deliveryData
    });
    return response.data || response;
  }

  async deleteDeliveryPerson(id) {
    const response = await this.makeRequest(`/admin-dev/delivery/${id}`, {
      method: 'DELETE'
    });
    return response.data || response;
  }

  // Paramètres
  async getSettings() {
    try {
      const response = await this.makeRequest('/admin-dev/settings');
      return response.data || response;
    } catch (error) {
      console.warn('Settings API not available:', error);
      return [];
    }
  }

  async updateSettings(settings) {
    const response = await this.makeRequest('/admin-dev/settings', {
      method: 'PUT',
      body: { settings }
    });
    return response.data || response;
  }

  // Promotions
  async getPromotions() {
    try {
      const response = await this.makeRequest('/admin-dev/promotions');
      return response.data || response;
    } catch (error) {
      console.warn('Promotions API not available:', error);
      return [];
    }
  }

  async createPromotion(promotionData) {
    const response = await this.makeRequest('/admin-dev/promotions', {
      method: 'POST',
      body: promotionData
    });
    return response.data || response;
  }

  async updatePromotion(id, promotionData) {
    const response = await this.makeRequest(`/admin-dev/promotions/${id}`, {
      method: 'PUT',
      body: promotionData
    });
    return response.data || response;
  }

  async deletePromotion(id) {
    const response = await this.makeRequest(`/admin-dev/promotions/${id}`, {
      method: 'DELETE'
    });
    return response.data || response;
  }

  // Bannières
  async getBanners() {
    try {
      const response = await this.makeRequest('/admin-dev/banners');
      return response.data || response;
    } catch (error) {
      console.warn('Banners API not available, using fallback data');
      return [];
    }
  }

  async createBanner(bannerData) {
    const response = await this.makeRequest('/admin-dev/banners', {
      method: 'POST',
      body: bannerData
    });
    return response.data || response;
  }

  async updateBanner(id, bannerData) {
    const response = await this.makeRequest(`/admin-dev/banners/${id}`, {
      method: 'PUT',
      body: bannerData
    });
    return response.data || response;
  }

  async deleteBanner(id) {
    const response = await this.makeRequest(`/admin-dev/banners/${id}`, {
      method: 'DELETE'
    });
    return response.data || response;
  }
}

// Instance singleton
const adminApi = new AdminApiService();
export default adminApi;